import * as React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, Download, Search } from 'lucide-react';
import type { Posicao } from '@wms/shared';
import { useMapa } from '@/hooks/consultas';
import { useSessao } from '@/hooks/useSessao';
import {
  useAntesDepois,
  useEnderecosInventario,
  useInventario,
  type LinhaAntesDepois,
} from '@/hooks/inventario';
import { cn, compararNatural, moeda, numero, plural } from '@/lib/utils';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Cartao, Esqueleto, Kpi } from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { NavInventario } from '@/componentes/inventario/NavInventario';
import { CartaoEndereco, useHoverCartao } from '@/componentes/inventario/CartaoEndereco';

/**
 * Antes × depois da integração: o mesmo rack desenhado duas vezes, lado a
 * lado — à esquerda a foto do congelamento (o que o ERP achava que tinha), à
 * direita o que ficou depois que a Integração de Estoque lançou o ajuste no
 * kardex. Os endereços que mudaram ficam marcados nos dois.
 *
 * Embaixo, a mesma informação em grade, com Excel. Só entram escopos já
 * integrados.
 */

interface Endereco {
  localizador: number;
  escopo: number;
  p: Posicao | undefined;
  linhas: LinhaAntesDepois[];
  antes: number;
  depois: number;
  ajuste: number;
  valor: number;
}

interface Rua {
  nome: string;
  enderecos: Endereco[];
  afetados: number;
  valor: number;
}

const COLUNAS_CSV: ColunaCsv<LinhaAntesDepois>[] = [
  { cabecalho: 'Escopo', valor: (l) => l.escopo, texto: true },
  { cabecalho: 'Rua', valor: (l) => l.rua ?? '', texto: true },
  { cabecalho: 'Endereço', valor: (l) => l.endereco ?? '', texto: true },
  { cabecalho: 'Localizador', valor: (l) => l.localizador, texto: true },
  { cabecalho: 'SKU', valor: (l) => l.produto, texto: true },
  { cabecalho: 'Descrição', valor: (l) => l.descricao ?? '' },
  { cabecalho: 'Antes (foto)', valor: (l) => l.antes },
  { cabecalho: 'Ajuste', valor: (l) => l.ajuste },
  { cabecalho: 'Depois', valor: (l) => l.depois },
  { cabecalho: 'Custo', valor: (l) => l.custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
  { cabecalho: 'Valor do ajuste', valor: (l) => l.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
];

export function InventarioAntesDepois(): JSX.Element {
  const { id: bruto } = useParams();
  const id = Number(bruto);
  const valido = Number.isInteger(id) && id > 0;
  const { temPermissao } = useSessao();
  const detalhe = useInventario(valido ? id : null);
  const dados = useAntesDepois(valido ? id : null);
  const enderecos = useEnderecosInventario(valido ? id : null);
  const mapa = useMapa(detalhe.data?.inventario.centro ?? null);
  const [ruaVista, definirRuaVista] = React.useState<string | null>(null);
  const [soAfetados, definirSoAfetados] = React.useState(true);
  const [busca, definirBusca] = React.useState('');
  const [realce, definirRealce] = React.useState<number | null>(null);

  const porId = React.useMemo(
    () => new Map((mapa.data?.posicoes ?? []).map((p) => [p.localizador, p])),
    [mapa.data],
  );

  const integrados = React.useMemo(
    () => new Set((detalhe.data?.escopos ?? []).filter((e) => e.etapa === 'integrado').map((e) => e.escopo)),
    [detalhe.data],
  );

  const ruas = React.useMemo<Rua[]>(() => {
    const porLoc = new Map<number, Endereco>();
    // Todo endereço dos escopos integrados entra no desenho, mesmo vazio e
    // sem ajuste — senão o rack fica com buraco onde a contagem confirmou zero.
    for (const e of enderecos.data?.enderecos ?? []) {
      if (!integrados.has(e.escopo)) continue;
      porLoc.set(e.localizador, {
        localizador: e.localizador,
        escopo: e.escopo,
        p: porId.get(e.localizador),
        linhas: [],
        antes: 0,
        depois: 0,
        ajuste: 0,
        valor: 0,
      });
    }
    for (const l of dados.data?.linhas ?? []) {
      const end =
        porLoc.get(l.localizador) ??
        ({
          localizador: l.localizador,
          escopo: l.escopo,
          p: porId.get(l.localizador),
          linhas: [],
          antes: 0,
          depois: 0,
          ajuste: 0,
          valor: 0,
        } satisfies Endereco);
      end.linhas.push(l);
      end.antes += l.antes;
      end.depois += l.depois;
      end.ajuste += l.ajuste;
      end.valor += l.valor;
      porLoc.set(l.localizador, end);
    }
    const g = new Map<string, Endereco[]>();
    for (const end of porLoc.values()) {
      const nome = end.p?.corredor.trim() || end.linhas[0]?.rua || '(fora do mapa)';
      const l = g.get(nome) ?? [];
      l.push(end);
      g.set(nome, l);
    }
    return [...g.entries()]
      .sort(([a], [b]) => compararNatural(a, b))
      .map(([nome, ends]) => ({
        nome,
        enderecos: ends,
        afetados: ends.filter((e) => e.linhas.some((l) => l.ajuste !== 0)).length,
        valor: ends.reduce((s, e) => s + e.valor, 0),
      }));
  }, [enderecos.data, dados.data, porId, integrados]);

  // Abre na rua com mais endereços mexidos.
  React.useEffect(() => {
    if (ruaVista && ruas.some((r) => r.nome === ruaVista)) return;
    const maior = [...ruas].sort((a, b) => b.afetados - a.afetados)[0];
    if (maior) definirRuaVista(maior.nome);
  }, [ruas, ruaVista]);

  const linhas = dados.data?.linhas ?? [];
  const termo = busca.trim().toUpperCase();
  const visiveis = linhas.filter(
    (l) =>
      (!soAfetados || l.ajuste !== 0) &&
      (!termo ||
        String(l.produto).includes(termo) ||
        String(l.localizador).includes(termo) ||
        String(l.escopo) === termo ||
        (l.endereco ?? '').toUpperCase().includes(termo) ||
        (l.descricao ?? '').toUpperCase().includes(termo)),
  );

  const entradas = linhas.filter((l) => l.ajuste > 0);
  const saidas = linhas.filter((l) => l.ajuste < 0);
  const somaValor = (ls: LinhaAntesDepois[]): number => ls.reduce((s, l) => s + l.valor, 0);
  const afetados = new Set(linhas.filter((l) => l.ajuste !== 0).map((l) => l.localizador)).size;
  const rua = ruas.find((r) => r.nome === ruaVista) ?? null;

  if (!valido) return <EstadoVazio titulo="Inventário inválido" />;
  if (detalhe.isError) return <EstadoErro erro={detalhe.error} />;
  if (dados.isError) return <EstadoErro erro={dados.error} aoTentarDeNovo={() => void dados.refetch()} />;
  const carregando = detalhe.isLoading || dados.isLoading || enderecos.isLoading || mapa.isLoading;

  return (
    <div className="space-y-4 p-3 sm:p-5 sm:pl-8">
      {detalhe.data ? (
        <NavInventario
          inventario={id}
          nome={detalhe.data.inventario.nome}
          podePlanejar={temPermissao('inventario.planejar')}
        />
      ) : null}
      <CabecalhoPagina
        sobrelinha={detalhe.data ? `Inventário #${id} · centro ${detalhe.data.inventario.centro}` : 'Inventário'}
        titulo="Antes × depois da integração"
        descricao="À esquerda a foto do congelamento; à direita o saldo depois do ajuste lançado pela Integração de Estoque."
      />

      {carregando ? (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-4">
            {[0, 1, 2, 3].map((k) => (
              <Esqueleto key={k} className="h-24" />
            ))}
          </div>
          <Esqueleto className="h-[360px]" />
        </div>
      ) : integrados.size === 0 ? (
        <EstadoVazio
          titulo="Nenhum escopo integrado ainda"
          detalhe="O antes × depois aparece quando a Integração de Estoque de algum escopo deste inventário for feita."
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
              rotulo="Endereços ajustados"
              valor={numero(afetados)}
              nota={`em ${numero(integrados.size)} ${plural(integrados.size, 'escopo integrado', 'escopos integrados')}`}
              tom="neutro"
            />
            <Kpi
              rotulo="Entradas (sobras)"
              valor={`+${numero(entradas.reduce((s, l) => s + l.ajuste, 0))} un`}
              nota={moeda(somaValor(entradas))}
              tom="ok"
            />
            <Kpi
              rotulo="Saídas (faltas)"
              valor={`${numero(saidas.reduce((s, l) => s + l.ajuste, 0))} un`}
              nota={moeda(somaValor(saidas))}
              tom="erro"
            />
            <Kpi
              rotulo="Resultado do inventário"
              valor={moeda(somaValor(linhas))}
              nota="último custo de compra, como no Resultado Consolidado do PBS"
              tom={somaValor(linhas) < 0 ? 'erro' : 'ok'}
            />
          </div>

          <Cartao className="overflow-hidden p-0">
            <div className="flex gap-1.5 overflow-x-auto border-b border-borda bg-superficie2 px-2.5 py-2">
              {ruas.map((r) => {
                const ativa = r.nome === ruaVista;
                return (
                  <button
                    key={r.nome}
                    type="button"
                    onClick={() => definirRuaVista(r.nome)}
                    aria-pressed={ativa}
                    className={cn(
                      'shrink-0 rounded-lg border px-2.5 py-1.5 text-left leading-tight transition-colors',
                      ativa ? 'border-primaria bg-primaria-suave' : 'border-borda bg-superficie hover:border-bordaForte',
                    )}
                  >
                    <span className={cn('block font-mono text-[12px] font-bold', ativa && 'text-primaria')}>
                      {r.nome}
                    </span>
                    <span className="tabular block text-[10.5px] text-textoFraco">
                      {r.afetados > 0 ? (
                        <>
                          {r.afetados} {plural(r.afetados, 'ajuste', 'ajustes')} ·{' '}
                          <b className={r.valor < 0 ? 'text-status-divergencia' : 'text-status-livre'}>{moeda(r.valor)}</b>
                        </>
                      ) : (
                        'sem ajuste'
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {rua ? (
              <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-start">
                <RackComparado rua={rua} lado="antes" realce={realce} />
                <ArrowRight className="mx-auto hidden h-6 w-6 self-center text-textoSuave lg:block" />
                <RackComparado rua={rua} lado="depois" realce={realce} />
              </div>
            ) : null}
            <Legenda />
          </Cartao>

          <Cartao className="overflow-hidden p-0">
            <div className="flex flex-wrap items-center gap-2 border-b border-borda px-4 py-3">
              <p className="text-sm font-semibold">
                {numero(visiveis.length)} {plural(visiveis.length, 'linha', 'linhas')}
              </p>
              <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={soAfetados}
                  onChange={(e) => definirSoAfetados(e.target.checked)}
                  className="h-4 w-4"
                />
                Só o que mudou
              </label>
              <div className="relative ml-auto">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
                <input
                  value={busca}
                  onChange={(e) => definirBusca(e.target.value)}
                  placeholder="SKU, endereço, descrição…"
                  className="h-9 w-64 rounded-lg border border-borda bg-superficie pl-8 pr-3 text-sm outline-none focus:border-primaria"
                />
              </div>
              <Botao
                variante="secundaria"
                tamanho="pequeno"
                disabled={visiveis.length === 0}
                onClick={() => baixarCsv(`inventario-${id}-antes-depois-${carimboArquivo()}`, visiveis, COLUNAS_CSV)}
              >
                <Download className="h-4 w-4" />
                Excel
              </Botao>
            </div>
            <div className="max-h-[480px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-superficie2 text-left text-2xs uppercase tracking-wide text-textoFraco">
                  <tr>
                    <th className="px-3 py-2">Escopo</th>
                    <th className="px-3 py-2">Endereço</th>
                    <th className="px-3 py-2">Produto</th>
                    <th className="px-3 py-2 text-right">Antes</th>
                    <th className="px-3 py-2 text-right">Ajuste</th>
                    <th className="px-3 py-2 text-right">Depois</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {visiveis.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-textoSuave">
                        Nada com esse filtro.
                      </td>
                    </tr>
                  ) : (
                    visiveis.map((l) => (
                      <tr
                        key={`${l.escopo}-${l.localizador}-${l.produto}`}
                        onMouseEnter={() => definirRealce(l.localizador)}
                        onMouseLeave={() => definirRealce(null)}
                        onClick={() => {
                          const r = ruas.find((x) => x.enderecos.some((e) => e.localizador === l.localizador));
                          if (r) definirRuaVista(r.nome);
                        }}
                        className="cursor-pointer border-t border-borda hover:bg-superficie2"
                      >
                        <td className="tabular px-3 py-1.5">{l.escopo}</td>
                        <td className="whitespace-nowrap px-3 py-1.5 font-mono">
                          {l.endereco ?? l.localizador}
                          <span className="ml-1 text-2xs text-textoSuave">LOC {l.localizador}</span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="line-clamp-1" title={l.descricao ?? ''}>
                            {l.descricao}
                          </span>
                          <span className="font-mono text-2xs text-textoSuave">SKU {l.produto}</span>
                        </td>
                        <td className="tabular px-3 py-1.5 text-right">{numero(l.antes)}</td>
                        <td
                          className={cn(
                            'tabular px-3 py-1.5 text-right font-semibold',
                            l.ajuste > 0 ? 'text-status-livre' : l.ajuste < 0 ? 'text-status-divergencia' : 'text-textoSuave',
                          )}
                        >
                          {l.ajuste > 0 ? '+' : ''}
                          {numero(l.ajuste)}
                        </td>
                        <td className="tabular px-3 py-1.5 text-right font-semibold">{numero(l.depois)}</td>
                        <td
                          className={cn(
                            'tabular whitespace-nowrap px-3 py-1.5 text-right',
                            l.valor < 0 ? 'text-status-divergencia' : l.valor > 0 ? 'text-status-livre' : 'text-textoSuave',
                          )}
                        >
                          {moeda(l.valor)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Cartao>
        </>
      )}
    </div>
  );
}

/** Um dos dois racks: pinta cada célula pelo saldo do lado escolhido. */
function RackComparado({
  rua,
  lado,
  realce,
}: {
  rua: Rua;
  lado: 'antes' | 'depois';
  realce: number | null;
}): JSX.Element {
  const { hover, props: hoverDe } = useHoverCartao<Endereco[]>();
  const comGeo = rua.enderecos.filter((e) => e.p?.geometriaConfiavel);
  const semGeo = rua.enderecos.filter((e) => !e.p?.geometriaConfiavel);
  const modulos = [...new Set(comGeo.map((e) => e.p!.moduloOrdem))].sort((a, b) => a - b);
  const niveis = [...new Set(comGeo.map((e) => e.p!.nivelRack))].sort((a, b) => b - a);
  const celula = new Map<string, Endereco[]>();
  for (const e of comGeo) {
    const k = `${e.p!.moduloOrdem}|${e.p!.nivelRack}`;
    const l = celula.get(k) ?? [];
    l.push(e);
    celula.set(k, l);
  }
  const nome = (e: Endereco): string => e.p?.descricao?.trim() || e.linhas[0]?.endereco || String(e.localizador);
  const classe = (es: Endereco[]): string => {
    const saldo = es.reduce((s, e) => s + (lado === 'antes' ? e.antes : e.depois), 0);
    const ajuste = es.reduce((s, e) => s + e.ajuste, 0);
    const mexeu = es.some((e) => e.linhas.some((l) => l.ajuste !== 0));
    if (lado === 'depois' && mexeu) {
      return ajuste > 0
        ? 'border-status-livre bg-status-livre'
        : ajuste < 0 || es.some((e) => e.linhas.some((l) => l.ajuste < 0))
          ? 'border-status-divergencia bg-status-divergencia'
          : 'border-status-atencao bg-status-atencao';
    }
    const base = saldo > 0 ? 'border-slate-400 bg-slate-400/70' : 'border-dashed border-borda bg-superficie2';
    return mexeu ? cn(base, 'ring-2 ring-status-atencao ring-offset-1 ring-offset-superficie') : base;
  };
  const destacado = (es: Endereco[]): boolean => realce !== null && es.some((e) => e.localizador === realce);

  return (
    <div className="min-w-0">
      <p className="mb-2 text-sm font-semibold">
        {lado === 'antes' ? 'Antes' : 'Depois'}{' '}
        <span className="font-normal text-textoFraco">
          · {lado === 'antes' ? 'foto do congelamento' : 'após a integração'} · rua {rua.nome}
        </span>
      </p>
      {comGeo.length > 0 ? (
        <div className="overflow-x-auto pb-1">
          <table className="border-separate border-spacing-1">
            <thead>
              <tr>
                <th />
                {modulos.map((m) => (
                  <th key={m} className="tabular w-7 text-2xs font-semibold text-textoFraco">
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {niveis.map((n) => (
                <tr key={n}>
                  <th className="tabular w-9 pr-1 text-right text-2xs font-semibold text-textoFraco">
                    {n === 0 ? 'piso' : `N${n}`}
                  </th>
                  {modulos.map((m) => {
                    const es = celula.get(`${m}|${n}`) ?? [];
                    if (es.length === 0) return <td key={m} className="h-6 w-7" />;
                    return (
                      <td key={m}>
                        <div
                          {...hoverDe(es)}
                          aria-label={es.map(nome).join(', ')}
                          className={cn(
                            'h-6 w-7 rounded-md border transition-transform',
                            classe(es),
                            destacado(es) && 'scale-125 ring-2 ring-primaria',
                          )}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {semGeo.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {semGeo.map((e) => (
            <span
              key={e.localizador}
              {...hoverDe([e])}
              className={cn(
                'rounded-md border px-2 py-0.5 font-mono text-2xs',
                classe([e]),
                destacado([e]) && 'ring-2 ring-primaria',
              )}
            >
              {nome(e)}
            </span>
          ))}
        </div>
      ) : null}
      {hover ? (
        <CartaoEndereco
          ponto={hover}
          titulo={lado === 'antes' ? 'Antes · foto do congelamento' : 'Depois · após a integração'}
          rodape={`Escopo ${hover.alvo[0]!.escopo}`}
          enderecos={hover.alvo.map((e) => ({
            localizador: e.localizador,
            nome: nome(e),
            linhas: e.linhas
              .filter((l) => (lado === 'antes' ? l.antes : l.depois) !== 0 || l.ajuste !== 0)
              .map((l) => ({
                produto: l.produto,
                descricao: l.descricao,
                quantidade: lado === 'antes' ? l.antes : l.depois,
                anterior: lado === 'depois' ? l.antes : undefined,
              })),
          }))}
        />
      ) : null}
    </div>
  );
}

function Legenda(): JSX.Element {
  const itens: [string, string][] = [
    ['border-slate-400 bg-slate-400/70', 'com saldo'],
    ['border-dashed border-borda bg-superficie2', 'vazio'],
    ['border-borda bg-superficie2 ring-2 ring-status-atencao', 'mudou (no antes)'],
    ['bg-status-livre', 'entrou saldo (sobra)'],
    ['bg-status-divergencia', 'saiu saldo (falta)'],
  ];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-borda px-4 py-2.5 text-2xs text-textoFraco">
      {itens.map(([c, r]) => (
        <span key={r} className="inline-flex items-center gap-1.5">
          <span className={cn('h-3 w-3.5 rounded-sm border', c)} />
          {r}
        </span>
      ))}
    </div>
  );
}
